/**
 * Product card 3D tilt — cursor-following rotate on front and cert back faces.
 * Front hover glow + back satin coords update on pointer move.
 */
(function () {
  'use strict';

  if (window.productCardTilt) return;

  const WRAPPER_SELECTOR = '.product-card-wrapper.product-card__flip-scene';
  const FLIPPED_CLASS = 'is-cert-flipped';
  const TILT_ACTIVE_CLASS = 'is-tilt-active';
  const FLIP_ANIMATING_CLASS = 'is-flip-animating';
  const FLIPPER_SELECTOR = '.product-card__flipper';

  const FACE = {
    front: {
      selector: '.product-card__face--front',
      reset: 'translateZ(1px)',
    },
    back: {
      selector: '.product-card__face--back',
      reset: 'rotateY(180deg) translateZ(1px)',
    },
  };

  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverCapableQuery = window.matchMedia('(hover: hover)');
  const mobileSliderQuery = window.matchMedia('(max-width: 989px)');

  let activeWrapper = null;
  let activeFace = null;
  let activeFaceConfig = null;
  let bounds = null;
  let moveHandler = null;
  let pendingMoveEvent = null;
  let rafId = 0;
  let lastPointer = { clientX: 0, clientY: 0 };
  const flipSettleHandlers = new WeakMap();
  const tiltConfigCache = new WeakMap();
  const boundsCache = new WeakMap();

  document.addEventListener(
    'mousemove',
    (event) => {
      lastPointer = { clientX: event.clientX, clientY: event.clientY };
    },
    { passive: true }
  );

  function invalidateBounds(wrapper) {
    if (wrapper) boundsCache.delete(wrapper);
    if (activeWrapper === wrapper) bounds = null;
  }

  function faceEl(wrapper, faceConfig) {
    return wrapper.querySelector(faceConfig.selector);
  }

  function tiltFaceConfig(wrapper) {
    return wrapper.classList.contains(FLIPPED_CLASS) ? FACE.back : FACE.front;
  }

  function tiltConfig(wrapper) {
    const cached = tiltConfigCache.get(wrapper);
    if (cached) return cached;

    const styles = getComputedStyle(wrapper);
    const divisor = parseFloat(styles.getPropertyValue('--product-card-tilt-divisor'));
    const angleFactor = parseFloat(styles.getPropertyValue('--product-card-tilt-angle-factor'));
    const maxAngle = parseFloat(styles.getPropertyValue('--product-card-tilt-max-angle'));
    const config = {
      divisor: Number.isFinite(divisor) && divisor > 0 ? divisor : 95,
      angleFactor: Number.isFinite(angleFactor) ? angleFactor : 1.6,
      maxAngle: Number.isFinite(maxAngle) && maxAngle > 0 ? maxAngle : 9,
    };
    tiltConfigCache.set(wrapper, config);
    return config;
  }

  function wrapperBounds(wrapper) {
    if (activeWrapper === wrapper && bounds) return bounds;
    const cached = boundsCache.get(wrapper);
    if (cached) return cached;
    const rect = wrapper.getBoundingClientRect();
    boundsCache.set(wrapper, rect);
    return rect;
  }

  function isMobileSlider(wrapper) {
    if (!mobileSliderQuery.matches) return false;
    const grid = wrapper.closest('.product-grid');
    return Boolean(grid && grid.classList.contains('slider'));
  }

  function canTilt(wrapper) {
    if (!wrapper || !wrapper.matches(WRAPPER_SELECTOR)) return false;
    if (wrapper.classList.contains(FLIP_ANIMATING_CLASS)) return false;
    if (reducedMotionQuery.matches || !hoverCapableQuery.matches) return false;
    if (wrapper.closest('.section-divider')) return false;
    if (isMobileSlider(wrapper)) return false;
    return true;
  }

  function resetFaceEl(el, faceConfig) {
    if (!el || !faceConfig) return;
    el.style.transform = faceConfig.reset;
  }

  function resetFace(wrapper, faceConfig) {
    resetFaceEl(faceEl(wrapper, faceConfig), faceConfig);
  }

  function resetAllFaces(wrapper) {
    resetFace(wrapper, FACE.front);
    resetFace(wrapper, FACE.back);
    resetReflectivity(wrapper);
  }

  function frontGlowEl(wrapper) {
    return wrapper.querySelector('.product-card__face--front .card.card--product-surface');
  }

  function backSheenEl(wrapper) {
    return wrapper.querySelector('.cert-card-back');
  }

  function updateReflectivity(wrapper, event, rect) {
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    const front = frontGlowEl(wrapper);
    if (front) {
      front.style.setProperty('--product-card-glow-x', `${x}%`);
      front.style.setProperty('--product-card-glow-y', `${y}%`);
    }

    const back = backSheenEl(wrapper);
    if (back) {
      back.style.setProperty('--product-card-sheen-x', `${x}%`);
      back.style.setProperty('--product-card-sheen-y', `${y}%`);
    }
  }

  function resetReflectivity(wrapper) {
    const front = frontGlowEl(wrapper);
    if (front) {
      front.style.removeProperty('--product-card-glow-x');
      front.style.removeProperty('--product-card-glow-y');
    }

    const back = backSheenEl(wrapper);
    if (back) {
      back.style.removeProperty('--product-card-sheen-x');
      back.style.removeProperty('--product-card-sheen-y');
    }
  }

  function applyTilt(wrapper, el, faceConfig, event, rect) {
    const { divisor, angleFactor, maxAngle } = tiltConfig(wrapper);
    const leftX = event.clientX - rect.left;
    const topY = event.clientY - rect.top;
    const center = {
      x: leftX - rect.width / 2,
      y: topY - rect.height / 2,
    };
    const distance = Math.sqrt(center.x ** 2 + center.y ** 2);
    const rawAngle = Math.log(Math.max(distance, 1)) * angleFactor;
    const angle = Math.min(rawAngle, maxAngle);

    el.style.transform = `${faceConfig.reset} rotate3d(${center.y / divisor}, ${-center.x / divisor}, 0, ${angle}deg)`;
    updateReflectivity(wrapper, event, rect);
  }

  function clearFlipSettleListener(wrapper) {
    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    const handler = flipSettleHandlers.get(wrapper);
    if (flipper && handler) {
      flipper.removeEventListener('transitionend', handler);
    }
    flipSettleHandlers.delete(wrapper);
  }

  function onFlipSettled(wrapper) {
    if (!wrapper) return;
    clearFlipSettleListener(wrapper);
    wrapper.classList.remove(FLIP_ANIMATING_CLASS);
    resumeIfHovered(wrapper);
  }

  function flipTransitionIsInstant(flipper) {
    if (reducedMotionQuery.matches) return true;
    const duration = parseFloat(getComputedStyle(flipper).transitionDuration);
    return !Number.isFinite(duration) || duration === 0;
  }

  function onFlipStart(wrapper) {
    if (!wrapper) return;

    clearFlipSettleListener(wrapper);
    invalidateBounds(wrapper);

    if (activeWrapper === wrapper) {
      stopTracking(false);
    } else {
      wrapper.classList.remove(TILT_ACTIVE_CLASS);
    }

    resetAllFaces(wrapper);
    resetReflectivity(wrapper);
    wrapper.classList.add(FLIP_ANIMATING_CLASS);

    const flipper = wrapper.querySelector(FLIPPER_SELECTOR);
    if (!flipper || flipTransitionIsInstant(flipper)) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => onFlipSettled(wrapper));
      });
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== flipper || event.propertyName !== 'transform') return;
      onFlipSettled(wrapper);
    };

    flipSettleHandlers.set(wrapper, onTransitionEnd);
    flipper.addEventListener('transitionend', onTransitionEnd);
  }

  function stopTracking(resetFaces) {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    pendingMoveEvent = null;

    if (activeWrapper) {
      activeWrapper.classList.remove(TILT_ACTIVE_CLASS);
      if (resetFaces) {
        resetAllFaces(activeWrapper);
      } else {
        resetReflectivity(activeWrapper);
      }
    }
    activeWrapper = null;
    activeFace = null;
    activeFaceConfig = null;
    bounds = null;
    if (moveHandler) {
      document.removeEventListener('mousemove', moveHandler);
      moveHandler = null;
    }
  }

  function rotateToMouse(event) {
    if (!activeWrapper || !activeFace || !activeFaceConfig) return;
    if (!canTilt(activeWrapper)) {
      stopTracking(true);
      return;
    }

    const rect = bounds || wrapperBounds(activeWrapper);
    if (!rect || rect.width === 0 || rect.height === 0) {
      invalidateBounds(activeWrapper);
      return;
    }

    const currentConfig = tiltFaceConfig(activeWrapper);
    if (currentConfig !== activeFaceConfig) {
      resetFaceEl(activeFace, activeFaceConfig);
      const nextFace = faceEl(activeWrapper, currentConfig);
      if (!nextFace) {
        stopTracking(true);
        return;
      }
      activeFace = nextFace;
      activeFaceConfig = currentConfig;
    }

    applyTilt(activeWrapper, activeFace, activeFaceConfig, event, rect);
  }

  function scheduleRotate(event) {
    pendingMoveEvent = event;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      if (pendingMoveEvent) rotateToMouse(pendingMoveEvent);
      pendingMoveEvent = null;
    });
  }

  function startTracking(wrapper, event) {
    if (!canTilt(wrapper)) return;

    stopTracking(false);
    invalidateBounds(wrapper);

    const faceConfig = tiltFaceConfig(wrapper);
    const face = faceEl(wrapper, faceConfig);
    if (!face) return;

    activeWrapper = wrapper;
    activeFace = face;
    activeFaceConfig = faceConfig;
    bounds = wrapper.getBoundingClientRect();
    boundsCache.set(wrapper, bounds);
    wrapper.classList.add(TILT_ACTIVE_CLASS);
    moveHandler = scheduleRotate;
    document.addEventListener('mousemove', moveHandler, { passive: true });
    scheduleRotate(event);
  }

  function resumeIfHovered(wrapper) {
    if (!canTilt(wrapper)) return;
    const hit = document.elementFromPoint(lastPointer.clientX, lastPointer.clientY);
    if (!hit || !wrapper.contains(hit)) return;
    startTracking(wrapper, lastPointer);
  }

  function resetWrapper(wrapper) {
    if (!wrapper) return;
    clearFlipSettleListener(wrapper);
    wrapper.classList.remove(FLIP_ANIMATING_CLASS);
    invalidateBounds(wrapper);
    if (activeWrapper === wrapper) {
      stopTracking(true);
      return;
    }
    wrapper.classList.remove(TILT_ACTIVE_CLASS);
    resetAllFaces(wrapper);
  }

  document.body.addEventListener('mouseover', (event) => {
    const wrapper = event.target.closest(WRAPPER_SELECTOR);
    if (!wrapper) return;

    const from = event.relatedTarget;
    if (from instanceof Node && wrapper.contains(from)) return;

    startTracking(wrapper, event);
  });

  document.body.addEventListener('mouseout', (event) => {
    const wrapper = event.target.closest(WRAPPER_SELECTOR);
    if (!wrapper || activeWrapper !== wrapper) return;

    const to = event.relatedTarget;
    if (to instanceof Node && wrapper.contains(to)) return;

    stopTracking(true);
  });

  window.addEventListener(
    'scroll',
    () => {
      if (activeWrapper) invalidateBounds(activeWrapper);
    },
    { passive: true, capture: true }
  );

  window.addEventListener('resize', () => {
    if (activeWrapper) invalidateBounds(activeWrapper);
  });

  window.productCardTilt = {
    reset: resetWrapper,
    stopAll: () => stopTracking(true),
    onFlipStart,
  };
})();
